import { type ReactNode, useMemo, useState } from 'react';
import { useCurrentUser } from '../auth/AuthProvider.js';
import { usePreference, useSessionValue } from '../lib/preferences.js';
import { Icon } from './Icon.js';
import { nextSort, sortRows } from '@k-management/shared';
import { ariaSort, SortButton } from './SortHeader.js';

export interface GridColumn<Row> {
  key: string;
  label: string;
  render: (row: Row) => ReactNode;
  /** Value for sorting and quick search; without it the column does neither. */
  value?: (row: Row) => string | number | null;
  align?: 'left' | 'right';
  /** Starts hidden: the user can show it from «Colonne». */
  hiddenByDefault?: boolean;
  /** Cannot be hidden (e.g. the name). */
  required?: boolean;
}

interface GridPreferences {
  hidden: string[] | null;
  sort: { key: string; direction: 1 | -1 } | null;
  density: 'comfortable' | 'compact';
}

/**
 * Rows in groups that open and close (decision of 29/09/2026). The rows of a
 * group come one after the other; the group is headed by one of its rows (a
 * type over its sub-types) or by a heading the grid adds (the costs of a type).
 */
export interface GridGroups<Row> {
  /** The group of a row; null for a row outside every group. */
  of: (row: Row) => string | null;
  /** The row heading its group; without it the grid adds a heading row. */
  isHead?: (row: Row) => boolean;
  /** Content of an added heading row. */
  label?: (group: string, rows: Row[]) => ReactNode;
}

/**
 * The common data grid (proposal §7): quick search, sortable columns, column
 * chooser, density, selection, pagination, groups. Columns, sort, density and
 * open groups are remembered per user in this browser.
 */
export function DataGrid<Row>({
  id,
  columns,
  rows,
  rowKey,
  searchPlaceholder = 'Cerca',
  toolbar,
  empty = 'Nessun elemento.',
  rowClassName,
  onRowOpen,
  openOnClick = false,
  actions,
  bulkActions,
  groups,
  pageSize = 25,
  filtersChanged = false,
  onResetFilters,
  searchable = true,
}: {
  id: string;
  columns: GridColumn<Row>[];
  rows: Row[];
  rowKey: (row: Row) => string;
  searchPlaceholder?: string;
  /** Filters and chips shown next to the search box. */
  toolbar?: ReactNode;
  empty?: ReactNode;
  rowClassName?: (row: Row) => string | undefined;
  onRowOpen?: (row: Row) => void;
  /**
   * A single click anywhere on the row opens it (Tesoreria, 07/10/2026), not
   * only the double click; not when the click is on a control or ends a text
   * selection.
   */
  openOnClick?: boolean;
  /** Commands of a row; `natural` says the rows are in their own order (no search, no sort). */
  actions?: (row: Row, view: { natural: boolean }) => ReactNode;
  /** Commands on the selected rows; without it rows cannot be selected. */
  bulkActions?: (selected: Row[], clear: () => void) => ReactNode;
  /** Groups closed at first: sorting by a column shows the plain list instead. */
  groups?: GridGroups<Row>;
  pageSize?: number;
  /** The filters of the page differ from their defaults: «Azzera filtri» shows (11/10/2026). */
  filtersChanged?: boolean;
  /** Puts the filters of the page back to their defaults; the search and the page go too. */
  onResetFilters?: () => void;
  /**
   * Without the search box, and the search remembered for the list set aside
   * (not lost): the rows given are the ones to see (a movement opened from an
   * invoice, 2.07).
   */
  searchable?: boolean;
}) {
  const user = useCurrentUser();
  const [preferences, setPreferences] = usePreference<GridPreferences>(`grid.${id}.${user.email}`, {
    hidden: null,
    sort: null,
    density: 'comfortable',
  });
  // Search and page found again when coming back to the list, while the browser stays open.
  const [view, setView] = useSessionValue(`grid.${id}.view.${user.email}`, { query: '', page: 0 });
  const query = searchable ? view.query : '';
  const page = searchable ? view.page : 0;
  const setQuery = (next: string) => setView({ query: next, page: 0 });
  const setPage = (next: number) => setView({ query: view.query, page: next });
  const [choosingColumns, setChoosingColumns] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [open, setOpen] = usePreference<string[]>(`grid.${id}.open.${user.email}`, []);

  const hidden = new Set(
    preferences.hidden ??
      columns.filter((column) => column.hiddenByDefault).map((column) => column.key),
  );
  const visible = columns.filter((column) => column.required || !hidden.has(column.key));
  const sortColumn = columns.find((column) => column.key === preferences.sort?.key && column.value);

  const filtered = useMemo(() => {
    const needle = normalise(query);
    const matching = needle
      ? rows.filter((row) =>
          columns.some(
            (column) => column.value && normalise(String(column.value(row) ?? '')).includes(needle),
          ),
        )
      : rows;
    if (!sortColumn || !preferences.sort) return matching;
    return sortRows(matching, preferences.sort, sortColumn.value);
  }, [rows, columns, query, sortColumn, preferences.sort]);

  // Groups only in the order of the rows: a sort by column makes a plain list.
  const grouped = groups && !sortColumn ? groups : null;
  const searching = Boolean(normalise(query));
  const groupKeys = grouped
    ? [...new Set(rows.map((row) => grouped.of(row)).filter((key): key is string => key !== null))]
    : [];
  // A search opens every group with a match.
  const expanded = (key: string) => searching || open.includes(key);
  const toggleGroup = (key: string) =>
    setOpen(open.includes(key) ? open.filter((item) => item !== key) : [...open, key]);

  // With groups every row is on one page: a group never splits.
  const pages = grouped ? 1 : Math.max(1, Math.ceil(filtered.length / pageSize));
  const currentPage = Math.min(page, pages - 1);
  const shown = grouped
    ? filtered
    : filtered.slice(currentPage * pageSize, (currentPage + 1) * pageSize);
  const columnCount =
    visible.length + (bulkActions ? 1 : 0) + (actions ? 1 : 0) + (grouped ? 1 : 0);
  const selectedRows = rows.filter((row) => selected.has(rowKey(row)));
  const allShownSelected = shown.length > 0 && shown.every((row) => selected.has(rowKey(row)));

  const natural = !normalise(query) && !sortColumn;

  // Ascending, descending, then back to the order of the rows themselves.
  const toggleSort = (key: string) =>
    setPreferences({ ...preferences, sort: nextSort(preferences.sort, key) });
  const toggleColumn = (key: string) => {
    const next = new Set(hidden);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    setPreferences({ ...preferences, hidden: [...next] });
  };
  const toggleRow = (key: string) => {
    const next = new Set(selected);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    setSelected(next);
  };
  const toggleShown = () => {
    const next = new Set(selected);
    for (const row of shown) {
      if (allShownSelected) next.delete(rowKey(row));
      else next.add(rowKey(row));
    }
    setSelected(next);
  };

  return (
    <div className={`data-grid ${preferences.density}`}>
      <div className="grid-toolbar">
        {searchable && (
          <label className="grid-search">
            <Icon name="search" size={16} />
            <input
              type="search"
              value={query}
              placeholder={searchPlaceholder}
              aria-label={searchPlaceholder}
              onChange={(event) => setQuery(event.target.value)}
            />
          </label>
        )}
        {toolbar}
        {(filtersChanged || query) && (
          <button
            type="button"
            className="small"
            onClick={() => {
              setQuery('');
              onResetFilters?.();
            }}
          >
            Azzera filtri
          </button>
        )}
        {grouped && groupKeys.length > 0 && !searching && (
          <>
            <button type="button" className="small" onClick={() => setOpen(groupKeys)}>
              Apri tutti
            </button>
            <button type="button" className="small" onClick={() => setOpen([])}>
              Chiudi tutti
            </button>
          </>
        )}
        <span className="grid-spacer" />
        <div className="segmented-small" role="group" aria-label="Densità">
          {(['comfortable', 'compact'] as const).map((density) => (
            <button
              key={density}
              type="button"
              className={preferences.density === density ? 'selected' : undefined}
              onClick={() => setPreferences({ ...preferences, density })}
            >
              {density === 'comfortable' ? 'Comoda' : 'Compatta'}
            </button>
          ))}
        </div>
        <button
          type="button"
          className={choosingColumns ? 'small selected' : 'small'}
          aria-expanded={choosingColumns}
          onClick={() => setChoosingColumns(!choosingColumns)}
        >
          <Icon name="columns" size={16} /> Colonne
        </button>
      </div>

      {choosingColumns && (
        <div className="grid-columns card">
          {columns.map((column) => (
            <label key={column.key} className="checkbox">
              <input
                type="checkbox"
                checked={column.required || !hidden.has(column.key)}
                disabled={column.required}
                onChange={() => toggleColumn(column.key)}
              />
              {column.label}
            </label>
          ))}
        </div>
      )}

      {bulkActions && selectedRows.length > 0 && (
        <div className="grid-bulk" role="status">
          <strong>
            {selectedRows.length === 1 ? '1 selezionato' : `${selectedRows.length} selezionati`}
          </strong>
          <span className="grid-spacer" />
          {bulkActions(selectedRows, () => setSelected(new Set()))}
          <button type="button" className="small" onClick={() => setSelected(new Set())}>
            Deseleziona
          </button>
        </div>
      )}

      <div className="table-wrap card flush">
        <table className="data-table">
          <thead>
            <tr>
              {grouped && <th className="grid-toggle" aria-label="Gruppi" />}
              {bulkActions && (
                <th className="grid-check">
                  <input
                    type="checkbox"
                    aria-label="Seleziona la pagina"
                    checked={allShownSelected}
                    onChange={toggleShown}
                  />
                </th>
              )}
              {visible.map((column) => {
                const sorted = preferences.sort?.key === column.key;
                return (
                  <th
                    key={column.key}
                    className={column.align === 'right' ? 'numeric' : undefined}
                    aria-sort={ariaSort(sorted ? preferences.sort!.direction : null)}
                  >
                    {column.value ? (
                      <SortButton
                        sorted={sorted ? preferences.sort!.direction : null}
                        onClick={() => toggleSort(column.key)}
                      >
                        {column.label}
                      </SortButton>
                    ) : (
                      column.label
                    )}
                  </th>
                );
              })}
              {actions && <th aria-label="Azioni" />}
            </tr>
          </thead>
          <tbody>
            {shown.map((row, index) => {
              const key = rowKey(row);
              const group = grouped?.of(row) ?? null;
              const head = group !== null && (grouped?.isHead?.(row) ?? false);
              const previous = index > 0 ? (grouped?.of(shown[index - 1]!) ?? null) : null;
              // An added heading before the first row of each group.
              const heading =
                grouped && group !== null && !grouped.isHead && group !== previous ? (
                  <tr key={`group-${group}`} className="grid-group">
                    <td colSpan={columnCount}>
                      <button
                        type="button"
                        className="grid-group-toggle"
                        aria-expanded={expanded(group)}
                        onClick={() => toggleGroup(group)}
                      >
                        <Icon name="right" size={14} />
                        {grouped.label?.(
                          group,
                          shown.filter((item) => grouped.of(item) === group),
                        ) ?? group}
                      </button>
                    </td>
                  </tr>
                ) : null;
              if (group !== null && !head && !expanded(group)) return heading;
              const line = (
                <tr
                  key={key}
                  className={
                    [
                      selected.has(key) ? 'selected' : '',
                      openOnClick && onRowOpen ? 'openable' : '',
                      rowClassName?.(row) ?? '',
                    ]
                      .join(' ')
                      .trim() || undefined
                  }
                  onClick={
                    openOnClick && onRowOpen
                      ? (event) => {
                          const target = event.target as HTMLElement;
                          if (target.closest('button, a, input, select, label, textarea')) return;
                          if (window.getSelection()?.toString()) return;
                          onRowOpen(row);
                        }
                      : undefined
                  }
                  onDoubleClick={onRowOpen && !openOnClick ? () => onRowOpen(row) : undefined}
                >
                  {grouped && (
                    <td className="grid-toggle">
                      {head && (
                        <button
                          type="button"
                          className="grid-group-toggle"
                          aria-expanded={expanded(group!)}
                          aria-label={expanded(group!) ? 'Chiudi il gruppo' : 'Apri il gruppo'}
                          onClick={() => toggleGroup(group!)}
                        >
                          <Icon name="right" size={14} />
                        </button>
                      )}
                    </td>
                  )}
                  {bulkActions && (
                    <td className="grid-check">
                      <input
                        type="checkbox"
                        aria-label="Seleziona"
                        checked={selected.has(key)}
                        onChange={() => toggleRow(key)}
                      />
                    </td>
                  )}
                  {visible.map((column) => (
                    <td
                      key={column.key}
                      className={column.align === 'right' ? 'numeric' : undefined}
                    >
                      {column.render(row)}
                    </td>
                  ))}
                  {actions && <td className="grid-actions">{actions(row, { natural })}</td>}
                </tr>
              );
              return heading ? [heading, line] : line;
            })}
            {shown.length === 0 && (
              <tr>
                <td className="grid-empty" colSpan={columnCount}>
                  {query ? 'Nessun risultato per questa ricerca.' : empty}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="grid-footer">
        <span className="muted">
          {filtered.length === 0
            ? '0 righe'
            : `${currentPage * pageSize + 1}–${Math.min((currentPage + 1) * pageSize, filtered.length)} di ${filtered.length}`}
        </span>
        {pages > 1 && (
          <div className="grid-pages">
            <button
              type="button"
              className="small"
              aria-label="Pagina precedente"
              disabled={currentPage === 0}
              onClick={() => setPage(currentPage - 1)}
            >
              <Icon name="left" size={14} />
            </button>
            <span>
              Pagina {currentPage + 1} di {pages}
            </span>
            <button
              type="button"
              className="small"
              aria-label="Pagina successiva"
              disabled={currentPage >= pages - 1}
              onClick={() => setPage(currentPage + 1)}
            >
              <Icon name="right" size={14} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function normalise(text: string): string {
  return text
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
}
